import { canApplyPurchaseSavePrice, purchasePriceSaveContext, samePurchaseSaveLine } from "../../utils/purchasePriceSaveGuard";
import { isParStockPurchase, parStockCoverageLabel } from "../../utils/purchaseRequisitionSource";
import { getLocationName } from "../../utils/locationNames";
import { supplierIdentityIds } from "../../utils/supplierIdentity";
/**
 * Palantir PMS - Purchase Order Edit Popup
 * Autor: Alberto Castro Ull
 */

import { useInlineDestinations, createDestinationLineKey, useDestinationSaveQueue } from "./ReceivingDestinations";
import { FiClock, FiCornerUpLeft, FiXCircle, FiFileText, FiRotateCcw, FiSlash, FiTrash2 } from "react-icons/fi";
import PurchaseOrderActions from "./PurchaseOrderActions";
import { orderHeaderSignature, orderLineSignature, hasOrderEditorChanges } from "../../utils/orderEditorChanges";
import { unifiedApprovalsText } from "../../pages/unifiedApprovalsMessages";
import React, { useEffect, useRef, useState } from "react";
import { TenderPriceLabel, purchasePriceSnapshot, usePurchaseTenderPrices } from "./TenderPriceFeedback";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentUnitName } from "../../utils/unitNames";
import { purchaseSupplierError, supplierMinimumLabel, getDocumentTranslator, documentStatus } from "./documentPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingDocumentPopups.css";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import useIsMobile from "../../../hooks/useIsMobile";
import PurchasingDocumentDialog, { useDocumentDialog } from "./PurchasingDocumentDialog";
import PurchaseOrderItemPicker from "./PurchaseOrderItemPicker";
import { usePOApprovalDecision } from "./POApprovalDecisionDialog";
import POApprovalHistory from "./POApprovalHistory";
import { getPurchaseOrderEditingTranslator } from "./purchaseOrderEditingMessages";

const editNumberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false,
});

const isUnsent = (order) => Boolean(order && !order.sent_at && !order.acknowledged_at);
const isReadyToSend = (order) => isUnsent(order) && order.status === "confirmed" && order.can_send_to_supplier === true;

function OrderLineNumberInput({ value, onChange, ...props }) {
  const [typedValue, setTypedValue] = useState(null);
  // Keep formatting out of the order data: merely opening/saving an editor must
  // not round a stored price. Once typed, preserve every decimal the user enters.
  const displayed = typedValue !== null && typedValue === value ? typedValue
    : value == null || String(value).trim() === "" || !Number.isFinite(Number(value)) ? ""
    : editNumberFormatter.format(Number(value));
  return <input {...props} type="number" step="any" value={displayed} onChange={(event) => {
    setTypedValue(event.target.value);
    onChange(event);
  }} />;
}

export default function EditOrderPopup({ orderId, isOpen, onClose, onApprovalChanged }) {
  const latestFormRef = useRef(null);
  const enqueueDestinationSave = useDestinationSaveQueue();
  const { themeId, languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const editText = getPurchaseOrderEditingTranslator(languageId);
  const numberFormatter = new Intl.NumberFormat(languageId === "es" ? "es-ES" : "en-GB", {
    minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: false,
  });
  const moneyFormatter = new Intl.NumberFormat(languageId === "es" ? "es-ES" : "en-GB", {
    minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false,
  });
  // Format only the presentation; keep the original precision in form/save data.
  const formatNumber = (value) => value == null || String(value).trim() === "" || !Number.isFinite(Number(value))
    ? "—" : numberFormatter.format(Number(value));
  const formatMoney = (value) => value == null || String(value).trim() === "" || !Number.isFinite(Number(value))
    ? "—" : moneyFormatter.format(Number(value));
  const { confirm: confirmDocument, notify: notifyDocument, dialog, isOpen: documentDialogOpen } = useDocumentDialog();
  const approvalDecision = usePOApprovalDecision();
  const isMobile = useIsMobile();
  const currentUser = readStoredUser();
  const canUpdateOrder = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.orders.update"] === true;
  const hasDeletePermission = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.orders.delete"] === true;
  const [form, setForm] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [pickerItems, setPickerItems] = useState([]);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const pickerRequest = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const documentSaveInFlight = useRef(false);
  const [units, setUnits] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sendConfirmationOpen, setSendConfirmationOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [savingLines, setSavingLines] = useState(0);
  const sendInFlight = useRef(false);
  const lineSavesInFlight = useRef(0);
  const currentForm = form && String(form.id) === String(orderId) ? form : null;
  const minimumLabel = isUnsent(currentForm) && supplierMinimumLabel(
    suppliers.find((supplier) => supplierIdentityIds(supplier).includes(Number(currentForm.supplier_id))) || currentForm.supplier,
    currentForm.currency, languageId
  );
  const canCompleteOwnDraft = currentUser?.permissions?.["purchasing.orders.create"] === true
    && Number(currentForm?.created_by) === Number(currentUser?.id)
    && currentForm?.status === "draft" && !currentForm.sent_at
    && Number(currentForm.approval_revision || 0) === 0
    && (!currentForm.approval_status || currentForm.approval_status === "not_required");
  const canEditOrder = (canUpdateOrder || canCompleteOwnDraft) && Boolean(currentForm) && !["pending_approval", "rejected", "cancelled"].includes(currentForm.status)
    && (currentForm.status === "returned" ? currentForm.can_edit === true : currentForm.can_edit !== false);
  const canReviewAndSubmit = currentForm?.can_review_and_submit === true && !currentForm.sent_at
    && (["draft", "returned"].includes(currentForm.status) || (currentForm.status === "confirmed" && currentForm.procurement_review_required === true));
  const confirmedUnsent = isUnsent(currentForm) && currentForm.status === "confirmed";
  const canSendToSupplier = canUpdateOrder && isReadyToSend(currentForm);
  const reviewError = (err) => ({
    PO_REVIEW_PRICE_REQUIRED: editText("Enter a valid nonnegative price or leave it blank."),
    PO_REVIEWER_REQUIRED: editText("You need permission to review and submit purchase orders, or be an applicable approver when no reviewer is configured."),
    PO_REVIEW_REQUIRED: editText("Procurement review is required. Reopen the order and ask an assigned reviewer to review and submit it."),
  }[err?.response?.data?.code]);
  const canDeleteOrder = hasDeletePermission && isUnsent(currentForm)
    && ["draft", "confirmed"].includes(currentForm.status)
    && Number(currentForm.approval_revision || 0) === 0;
  const canCancelOrder = isUnsent(currentForm)
    && ["draft", "pending_approval", "returned", "rejected", "confirmed"].includes(currentForm.status)
    && currentForm.can_cancel === true;
  const canAddItems = canEditOrder && ["draft", "returned", "confirmed"].includes(currentForm.status) && isUnsent(currentForm) && Boolean(currentForm.supplier_id);
  usePurchaseTenderPrices({ form: currentForm, setForm, documentType: "order", enabled: isOpen && canEditOrder && currentForm?.status === "draft", businessUnitId: currentUser?.active_business_unit_id });
  const currentApprovalRun = (currentForm?.approval_runs || []).find((run) => run.status === "pending");
  const pendingApproval = currentForm?.status === "pending_approval";
  const approvalText = unifiedApprovalsText(languageId);
  const pendingApproverNames = (currentApprovalRun?.tasks || [])
    .filter(task => task.status === "pending" && Number(task.level_number || 1) === Number(currentApprovalRun.current_level || 1))
    .map(task => task.approver?.name || task.approver?.email)
    .filter(Boolean);
  const canRecallOrder = currentForm?.can_recall === true
    && currentForm.status === "pending_approval" && currentForm.approval_status === "pending"
    && !currentForm.sent_at && !currentForm.approved_at && !currentForm.approved_by && Boolean(currentApprovalRun);
  const currentApprovalTask = currentApprovalRun?.tasks?.find(
    (task) =>
      currentForm?.can_approve !== false &&
      currentForm?.procurement_review_required !== true && currentForm?.review_method !== "automatic" &&
      task.status === "pending" &&
      Number(task.level_number || 1) === Number(currentApprovalRun.current_level || 1) &&
      Number(task.approver_user_id) === Number(currentUser?.id)
  );
  const formatUserLabel = (user) => {
    if (!user) return "-";
    return user.name || user.email || (user.id ? t("User #{0}", { 0: user.id }) : "-");
  };
  const formatDocumentInitiator = (record) => {
    if (isParStockPurchase(record)) return parStockCoverageLabel(languageId);
    const label = formatUserLabel(record?.initiated_by_user || record?.initiatedByUser);
    if (label !== "-") return label;
    const userId = record?.initiated_by_user_id || record?.initiatedByUserId;
    return userId ? t("User #{0}", { 0: userId }) : "-";
  };

  const normalizeOrderForm = (order) => ({
    ...order,
    __savedHeader: orderHeaderSignature(order),
    removedLineIds: [],
    lines: (order.lines || []).map((l) => ({
      ...l,
      __savedSignature: orderLineSignature(l),
      __locked: true,
      __editable: false,
      __amended: false,
      _destinationKey: l._destinationKey || (l.id ? undefined : createDestinationLineKey()),
      __isNew: false,
    })),
  });

  const refreshOrder = async () => {
    const res = await axios.get(`/api/purchasing/orders/${orderId}`);
    setForm(normalizeOrderForm(res.data));
    return res.data;
  };

  // Totals are derived by the server when lines change; opening a document is read-only.

  // 🧠 Cargar datos de la orden + catálogos
  useEffect(() => {
    if (!isOpen || !orderId) return;
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");
      setForm(null);
      setSendConfirmationOpen(false);
      setSendError("");
      setItemPickerOpen(false);
      setPickerItems([]);
      setPickerLoading(false);

      try {
        const currentUser = JSON.parse(localStorage.getItem("palantir_user"));
        const businessUnitId = currentUser?.active_business_unit_id;

        const [orderRes, supRes, itemRes, unitRes, locationsRes] = await Promise.all([
          axios.get(`/api/purchasing/orders/${orderId}`),
          axios.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`),
          axios.get(`/api/purchasing/items?business_unit_id=${businessUnitId}`),
          axios.get(`/api/purchasing/units?business_unit_id=${businessUnitId}`),
          axios.get("/api/purchasing/inventory/locations?include_inactive=false"),
        ]);

        if (cancelled) return;

        setForm(normalizeOrderForm(orderRes.data));


        setSuppliers(supRes.data || []);
        setItems(itemRes.data || []);
        setUnits(unitRes.data || []);
        setInventoryLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);

        console.log("[EditOrderPopup] ✅ Loaded order:", orderRes.data.order_code);
      } catch (err) {
        if (cancelled) return;
        console.error("❌ [EditOrderPopup] Error loading data:", err);
        setError("Error loading order data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      pickerRequest.current += 1;
    };
  }, [isOpen, orderId]);

  const renderLineLocation = useInlineDestinations({
    lines: form?.lines || [], items, locations: inventoryLocations,
    headerLocationId: form?.destination_location_id, scopeKey: form?.id, disabled: !isOpen || !canEditOrder || isSaving || isSending,
    onChange: (index, destination, source) => {
      if (!canEditOrder || documentSaveInFlight.current || sendInFlight.current) return;
      const line = form?.lines[index];
      if (!line) return;
      const nextLine = { ...line, destination_location_id: destination, destination_location_source: source, __amended: true };
      setForm((previous) => ({ ...previous, lines: previous.lines.map((entry, i) => i === index ? nextLine : entry) }));
      if (!confirmedUnsent) autoSaveLine(index, nextLine);
    },
  });

  if (!isOpen) return null;

  const incompleteLine = (line) => !line.item_id || !Number.isFinite(Number(line.quantity)) || Number(line.quantity) <= 0;
  const validateOrderLines = () => {
    if ((currentForm?.lines || []).some((line) => (line.__isNew || !line.id || line.__editable) && incompleteLine(line))) {
      setError(editText("Save or remove incomplete lines before continuing."));
      return false;
    }
    setError("");
    return true;
  };
  const saveOrderChanges = (updates = {}) =>
    axios.patch(`/api/purchasing/orders/${orderId}`, {
      approval_revision: form.approval_revision ?? 0,
      expected_delivery_date: form.expected_delivery_date,
      destination_location_id: form.destination_location_id || null,
      notes: form.notes,
      lines: form.lines.map(({ __savedSignature, ...line }) => line),
      ...(confirmedUnsent && form.removedLineIds.length ? { removed_line_ids: form.removedLineIds } : {}),
      ...updates,
    });

  const openItemPicker = async () => {
    if (!canAddItems || pickerLoading || lineSavesInFlight.current || documentSaveInFlight.current) return;
    const request = ++pickerRequest.current;
    setPickerLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/api/purchasing/user-item-access-rules/allowed-items");
      if (request !== pickerRequest.current) return;
      if (!Array.isArray(data?.allowed_item_ids)) throw new Error("Invalid item access response");
      const allowedIds = new Set(data.allowed_item_ids.map(String));
      setPickerItems(items.filter((item) => allowedIds.has(String(item.id))));
      setItemPickerOpen(true);
    } catch (err) {
      if (request === pickerRequest.current) setError(editText("Could not load the available items. Please try again."));
    } finally {
      if (request === pickerRequest.current) setPickerLoading(false);
    }
  };

  const addItem = (item) => {
    if (!canAddItems || !pickerItems.some((entry) => String(entry.id) === String(item.id))) return;
    setForm((previous) => {
      if (previous.lines.some((line) => String(line.item_id) === String(item.id))) return previous;
      return { ...previous, lines: [...previous.lines, {
        _destinationKey: createDestinationLineKey(),
        item_id: item.id, item, supplier_id: previous.supplier_id,
        unit_id: item.unit_id || null, unit: item.unit,
        subunit_id: item.subunit_id || null, subunit: item.subunit,
        pack_size: item.pack_size || 1, quantity: "", unit_price: "",
        __locked: false, __editable: true, __isNew: true,
      }] };
    });
  };

  const handleClose = async () => {
    const hasAmendment = hasOrderEditorChanges(currentForm);
    const prompt = hasAmendment ? "Discard the unsaved order changes and close?"
      : currentForm?.lines.some((line) => !line.id) ? "Discard the new items and close?" : null;
    if (prompt && !(await confirmDocument(editText(prompt)))) return;
    onClose();
  };

  const handleApprovalAction = async (decision) => {
    if (!currentApprovalTask || currentForm?.status !== "pending_approval" || documentSaveInFlight.current) return;
    const revision = currentForm.approval_revision ?? 0;
    documentSaveInFlight.current = true;
    setIsSaving(true);
    try {
      let comments;
      if (decision === "approve") {
        if (!(await confirmDocument(t("Approve Purchase Order {0}?", { 0: currentForm.order_code }), { tone: "success" }))) return;
      } else {
        comments = await approvalDecision.ask(decision, currentForm.order_code);
        if (comments === null) return;
      }
      await axios.post(`/api/purchasing/po-approval-rules/orders/${currentForm.id}/${decision}`, {
        approval_revision: revision,
        ...(decision === "return" ? { comments } : decision === "reject" ? { reason: comments } : {}),
      });
      // The decision is committed even if reloading the document later fails.
      onApprovalChanged?.();
      await refreshOrder();
      await notifyDocument(t(decision === "return" ? "Purchase Order returned for correction."
        : decision === "reject" ? "Purchase Order rejected." : "Purchase Order approved."));
      if (decision === "approve") onClose();
    } catch (err) {
      await notifyDocument(purchaseSupplierError(err, languageId) || err.response?.data?.error || t("Could not update the approval."));
    } finally {
      documentSaveInFlight.current = false;
      setIsSaving(false);
    }
  };

  const handleRecall = async () => {
    if (!canRecallOrder || documentSaveInFlight.current || lineSavesInFlight.current || sendInFlight.current) return;
    const revision = currentForm.approval_revision ?? 0;
    documentSaveInFlight.current = true;
    setIsSaving(true);
    let recovered = false;
    try {
      if (!(await confirmDocument(t("Recover order {0}? Its pending approval will be cancelled and the order will return to draft for editing.", { 0: currentForm.order_code })))) return;
      await axios.post(`/api/purchasing/po-approval-rules/orders/${currentForm.id}/recall`, { approval_revision: revision });
      recovered = true;
      setForm((previous) => previous?.id === currentForm.id ? { ...previous, can_recall: false } : previous);
      await refreshOrder();
      await notifyDocument(editText("Order recovered to draft. An assigned procurement reviewer must review and submit it again."));
    } catch (err) {
      // An approver may have acted while the confirmation was open. Refresh
      // the capabilities so a now-final order no longer offers recovery.
      try { await refreshOrder(); } catch (_) { /* Keep the current document and show the original error. */ }
      await notifyDocument(recovered
        ? t("The order was recovered, but its details could not be refreshed. Close and reopen it.")
        : err.response?.data?.error || t("Could not recover this purchase order. Reload it and try again."));
    } finally {
      documentSaveInFlight.current = false;
      setIsSaving(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!canCancelOrder || documentSaveInFlight.current || lineSavesInFlight.current || sendInFlight.current) return;
    documentSaveInFlight.current = true;
    setIsSaving(true);
    try {
      if (!(await confirmDocument(editText("Cancel purchase order {0}? This will discard any unsaved changes and close any pending approvals.", { 0: currentForm.order_code })))) return;
      const { data } = await axios.post(`/api/purchasing/orders/${currentForm.id}/cancel`, { approval_revision: currentForm.approval_revision ?? 0 });
      setForm(normalizeOrderForm(data));
      setError("");
      onApprovalChanged?.();
      await notifyDocument(t("Purchase order cancelled"));
    } catch (err) {
      if (err.response?.status === 409) {
        try { await refreshOrder(); onApprovalChanged?.(); } catch (_) { /* Show the original conflict. */ }
      }
      await notifyDocument(err.response?.data?.error || editText("Could not cancel this purchase order."));
    } finally {
      documentSaveInFlight.current = false;
      setIsSaving(false);
    }
  };

  const handleReviewAndSubmit = async () => {
    if (!canReviewAndSubmit || lineSavesInFlight.current || documentSaveInFlight.current || !validateOrderLines()) return;
    const invalidPrice = (currentForm.lines || []).some((line) => !Number.isFinite(Number(line.unit_price ?? 0)) || Number(line.unit_price) < 0);
    if (invalidPrice) { setError(editText("Enter a valid nonnegative price or leave it blank.")); return; }
    documentSaveInFlight.current = true;
    setIsSaving(true); setError("");
    try {
      // The server resolves the review role and any permitted fallback. Reviewers
      // without edit access submit persisted content without a CRUD write.
      const savedOrder = canEditOrder ? (await saveOrderChanges()).data : currentForm;
      setForm(normalizeOrderForm(savedOrder));
      if (confirmedUnsent && (savedOrder.status === "pending_approval" || isReadyToSend(savedOrder))) {
        onApprovalChanged?.();
        await notifyDocument(savedOrder.status === "pending_approval" ? t("Purchase Order sent for approval.") : t("✅ Order updated successfully."));
        return;
      }
      const response = await axios.post(`/api/purchasing/orders/${currentForm.id}/review-and-submit`, {
        approval_revision: savedOrder.approval_revision ?? currentForm.approval_revision ?? 0,
      });
      const reviewed = response.data.order || response.data;
      setForm(normalizeOrderForm(reviewed));
      onApprovalChanged?.();
      await notifyDocument(reviewed.status === "pending_approval" ? t("Purchase Order sent for approval.")
        : editText("Order reviewed. No approval is required; it is ready to send to the supplier."));
    } catch (err) {
      setError(reviewError(err) || purchaseSupplierError(err, languageId) || err.response?.data?.error || t("Error confirming order."));
    } finally {
      documentSaveInFlight.current = false;
      setIsSaving(false);
    }
  };

  const openSendConfirmation = () => {
    if (!canSendToSupplier || documentDialogOpen || documentSaveInFlight.current || sendInFlight.current || lineSavesInFlight.current || !validateOrderLines()) return;
    setSendError("");
    setSendConfirmationOpen(true);
  };

  const approvalNoticeAfterSave = (order) => {
    if (order.status === "pending_approval") return t("Purchase Order sent for approval.");
    if (order.status === "draft" || order.procurement_review_required === true) return editText("The updated order needs procurement review before it can be sent.");
    return editText("Order changes saved ({0}).", { 0: documentStatus(order.status, t) });
  };

  const handleSendToSupplier = async () => {
    if (!canSendToSupplier || !currentForm || sendInFlight.current) return;
    sendInFlight.current = true;
    setIsSending(true);
    setSendError("");
    let changesSaved = false;

    try {
      const { data: savedOrder } = await saveOrderChanges();
      changesSaved = true;
      // Keep persisted line ids for retries, along with the item/unit labels in the editor.
      setForm(normalizeOrderForm({
        ...savedOrder,
        lines: (savedOrder.lines || currentForm.lines).map((line) => ({
          ...currentForm.lines.find((existing) => existing.id === line.id),
          ...line,
        })),
      }));
      if (!isReadyToSend(savedOrder)) {
        setSendConfirmationOpen(false);
        onApprovalChanged?.();
        await notifyDocument(approvalNoticeAfterSave(savedOrder));
        return;
      }
      await axios.post(`/api/purchasing/orders/${currentForm.id}/send-to-supplier`, { approval_revision: savedOrder.approval_revision ?? currentForm.approval_revision ?? 0 });
    } catch (err) {
      console.error("❌ [PO][Send] Error saving or sending to supplier:", err);
      const detail = reviewError(err) || purchaseSupplierError(err, languageId) || err.response?.data?.error;
      setSendError(changesSaved
        ? detail || t("The order was saved, but the email could not be sent. Please try again.")
        : t("The order could not be saved and has not been sent.{0}", { 0: detail ? ` ${detail}` : " Please try again." }));
      if ((changesSaved && err.response?.status === 409) || ["PO_REVIEW_REQUIRED", "PO_REVIEWER_REQUIRED"].includes(err.response?.data?.code)) {
        try {
          const latest = await refreshOrder();
          onApprovalChanged?.();
          if (err.response?.data?.reason === "order_changed" && isReadyToSend(latest)) {
            setSendConfirmationOpen(false);
            await notifyDocument(editText("The order changed. Review the updated items before sending."));
          }
        } catch (refreshError) {
          console.error("❌ [PO][Send] Error refreshing approval status:", refreshError);
        }
      }
      return;
    } finally {
      sendInFlight.current = false;
      setIsSending(false);
    }

    setSendConfirmationOpen(false);
    setForm((prev) => ({ ...prev, status: "sent" }));
    onClose({
      action: "sent_to_supplier",
      orderCode: currentForm.order_code,
      supplierEmail: currentForm.supplier?.email,
    });
  };

  // 🔹 Guardar cambios manuales
  const handleSave = async () => {
    if (!canEditOrder || lineSavesInFlight.current || documentSaveInFlight.current || !validateOrderLines()) return;
    documentSaveInFlight.current = true;
    setIsSaving(true);
    try {
      const res = await saveOrderChanges();
      if (res.data?.id) setForm(normalizeOrderForm(res.data));
      if (confirmedUnsent && res.data?.id && !isReadyToSend(res.data)) {
        onApprovalChanged?.();
        await notifyDocument(approvalNoticeAfterSave(res.data));
        return;
      }
      await notifyDocument(t("✅ Order updated successfully."));
      onClose();
    } catch (err) {
      console.error("❌ [EditOrderPopup] Error saving order:", err);
      await notifyDocument(purchaseSupplierError(err, languageId) || err.response?.data?.error || t("Error saving order."));
    } finally {
      documentSaveInFlight.current = false;
      setIsSaving(false);
    }
  };

  // 🔹 Actualizar campo de cabecera
  const handleChange = (e) => {
    if (!canEditOrder) return;
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Actualizar línea individual
  const handleLineChange = (i, field, value) => {
    if (!canEditOrder) return;
    const updated = [...form.lines];
    updated[i] = { ...updated[i], [field]: value, __amended: true };
    if (field === "item_id" && Number(form.lines[i]?.item_id) !== Number(value)) {
      updated[i].destination_location_id = null;
      updated[i].destination_location_source = null;
    }
    setForm((prev) => ({ ...prev, lines: updated }));
  };

  // 🗑️ Eliminar línea sincronizada con BD
  const handleDeleteLine = async (index) => {
    if (!canEditOrder || lineSavesInFlight.current || documentSaveInFlight.current) return;
    const line = form.lines[index];
    if (!(await confirmDocument(t("Delete this line?")))) return;

    if (confirmedUnsent) {
      setForm((previous) => ({
        ...previous,
        removedLineIds: line.id ? [...previous.removedLineIds, line.id] : previous.removedLineIds,
        lines: previous.lines.filter((entry) => line.id ? entry.id !== line.id : entry._destinationKey !== line._destinationKey),
      }));
      return;
    }

    lineSavesInFlight.current += 1;
    setSavingLines(lineSavesInFlight.current);
    try {
      if (line.id) {
        await axios.delete(`/api/purchasing/orders/order-lines/${line.id}`, { params: { approval_revision: form.approval_revision ?? 0 } });
        console.log(`[PO] Line ${line.id} deleted from backend`);
      }

      setForm((prev) => prev?.id !== form.id || (prev.approval_revision ?? 0) !== (form.approval_revision ?? 0) ? prev : ({
        ...prev,
        lines: prev.lines.filter((entry) => line.id ? entry.id !== line.id : entry._destinationKey !== line._destinationKey),
      }));
    } catch (err) {
      console.error("❌ Error deleting line:", err);
      await notifyDocument(err.response?.data?.error || t("Error deleting line."));
    } finally {
      lineSavesInFlight.current -= 1;
      setSavingLines(lineSavesInFlight.current);
    }
  };

  latestFormRef.current = form;
  const autoSaveLine = (index, overrideLine) => {
    const line = overrideLine || latestFormRef.current?.lines[index];
    if (!canEditOrder || !line || documentSaveInFlight.current || sendInFlight.current) return Promise.resolve(false);
    const revision = latestFormRef.current?.approval_revision ?? 0;
    const documentId = latestFormRef.current?.id;
    const key = line._destinationKey || line.id || `item-${line.item_id}`;
    // Count queued saves immediately so a document save cannot overtake a new line.
    lineSavesInFlight.current += 1;
    setSavingLines(lineSavesInFlight.current);
    return enqueueDestinationSave(key, () => {
      if (latestFormRef.current?.id !== documentId || (latestFormRef.current?.approval_revision ?? 0) !== revision) return false;
      const currentIndex = (latestFormRef.current?.lines || []).findIndex((entry) => (entry._destinationKey || entry.id || `item-${entry.item_id}`) === key);
      if (currentIndex < 0) return false;
      return persistOrderLine(currentIndex, overrideLine);
    }).finally(() => {
      lineSavesInFlight.current -= 1;
      setSavingLines(lineSavesInFlight.current);
    });
  };
  const persistOrderLine = async (index, overrideLine) => {
    try {
        const savingForm = latestFormRef.current;
        if (!savingForm?.id || ["pending_approval", "rejected", "cancelled"].includes(savingForm.status) || (savingForm.status === "returned" && savingForm.can_edit !== true)) return;
        const currentLine = savingForm.lines[index];
        const line = overrideLine ? { ...overrideLine, id: currentLine.id } : currentLine;

        // ⚠️ No guardar líneas vacías
        if (!line?.item_id || !line.quantity) return false;
        if (incompleteLine(line)) {
          setError(editText("Save or remove incomplete lines before continuing."));
          return false;
        }
        setError("");
        const saveContext = purchasePriceSaveContext(savingForm, line, "order");

        const { __savedSignature, ...savedLineFields } = line;
        const payload = {
        order_id: savingForm.id,
        ...savedLineFields,
        approval_revision: savingForm.approval_revision ?? 0,
        supplier_id: savingForm.supplier_id, // hereda el proveedor
        destination_location_id: line.destination_location_id || savingForm.destination_location_id || null,
        total_price:
            line.quantity && line.unit_price
            ? parseFloat(line.quantity) * parseFloat(line.unit_price)
            : 0,
        };

        console.log("[PO][AutoSaveLine] Sending payload:", payload);

        const res = await axios.post(`/api/purchasing/orders/order-lines/autosave`, payload);
        console.log(`[PO][AutoSaveLine] ✅ Line ${index + 1} saved`, res.data);

        // 🟢 Guardar id devuelto en la línea correspondiente
        if (!res.data?.id) throw new Error("Saved order line has no ID");
        if (res.data?.id) {
          setForm((prev) => {
            if ((prev?.approval_revision ?? 0) !== (savingForm.approval_revision ?? 0)) return prev;
            const savedIndex = (prev?.lines || []).findIndex((entry) => samePurchaseSaveLine(prev, entry, saveContext));
            if (savedIndex < 0) return prev;
            const newLines = [...prev.lines];
            const currentLine = newLines[savedIndex];
            const acceptPrice = canApplyPurchaseSavePrice(prev, currentLine, saveContext, "order", res.data);
            const price = res.data.unit_price ?? currentLine.unit_price;
            newLines[savedIndex] = {
              ...currentLine,
              __savedSignature: orderLineSignature({ ...line,
                unit_price: res.data.unit_price ?? line.unit_price,
                destination_location_id: line.destination_location_id || res.data.destination_location_id || null,
              }),
              id: res.data.id,
              __isNew: false,
              ...(!currentLine.destination_location_id && res.data.destination_location_id ? { destination_location_source: currentLine.destination_location_source || "suggested" } : {}),
              destination_location_id: currentLine.destination_location_id || res.data.destination_location_id || null,
              ...(acceptPrice ? { ...purchasePriceSnapshot(res.data), unit_price: price, total_price: Number(currentLine.quantity || 0) * Number(price || 0) } : {}),
              ...(acceptPrice && Number(currentLine.quantity) === Number(line.quantity)
                ? { __locked: true, __editable: false, __isNew: false } : {}),
            };
            return { ...prev, lines: newLines };
          });
        }
        return true;

    } catch (err) {
        console.error(`❌ [PO][AutoSaveLine] Error saving line ${index + 1}:`, err);
        setError(purchaseSupplierError(err, languageId) || err.response?.data?.error || editText("Could not save this order line. Please try again."));
        return false;
    }
    };





  const calculateTotal = () => {
    return (
      form?.lines?.reduce((sum, l) => {
        const q = parseFloat(l.quantity || 0);
        const p = parseFloat(l.unit_price || 0);
        return sum + q * p;
      }, 0) || 0
    );
  };

  const getLineTotal = (line) => {
    const quantity = parseFloat(line.quantity || 0);
    const unitPrice = parseFloat(line.unit_price || 0);
    return formatMoney(quantity * unitPrice);
  };

  const getPackagingLabel = (line) => {
    const unit = getDocumentUnitName(line.unit, line.unit_id, units, languageId);
    const subunit = getDocumentUnitName(line.subunit, line.subunit_id, units, languageId);
    const pack = Number(line.pack_size || 1);

    if (!unit && !subunit) return "—";
    if (pack > 1) {
      return `${unit || "?"} (${formatNumber(pack)} x ${subunit || unit})`;
    }
    return unit || subunit || "—";
  };

  const getLineItemLabel = (line) => {
    if (line.item?.code) return `${line.item.code} - ${line.item.name}`;
    if (line.item?.name) return line.item.name;
    const selectedItem = items.find((item) => String(item.id) === String(line.item_id || ""));
    if (selectedItem?.code) return `${selectedItem.code} - ${selectedItem.name}`;
    return selectedItem?.name || "—";
  };

  const setLineEditable = (index, editable) => {
    if (!canEditOrder) return;
    setForm((prev) => {
      if (!prev?.lines?.[index]) return prev;
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], __locked: true, __editable: editable };
      return { ...prev, lines };
    });
  };

  const handleSaveLine = async (index) => {
    if (!canEditOrder || lineSavesInFlight.current || documentSaveInFlight.current) return;
    if (incompleteLine(form.lines[index])) {
      setError(editText("Save or remove incomplete lines before continuing."));
      return;
    }
    if (confirmedUnsent) {
      setError("");
      setLineEditable(index, false);
    } else {
      await autoSaveLine(index);
    }
  };

  const handleDeleteOrder = async () => {
    if (!canDeleteOrder) return;
    if (!(await confirmDocument(t("Are you sure you want to permanently delete order {0}?", { 0: currentForm.order_code })))) return;
    try {
      await axios.delete(`/api/purchasing/orders/${currentForm.id}`, { params: { approval_revision: currentForm.approval_revision ?? 0 } });
      await notifyDocument(t("Order {0} deleted successfully.", { 0: currentForm.order_code }));
      onClose();
    } catch (err) {
      console.error("❌ [PO][Delete] Error:", err);
      await notifyDocument(t("Error deleting the order. Check console for details."));
    }
  };
  const actionsBusy = isSaving || savingLines > 0 || isSending || sendConfirmationOpen || documentDialogOpen || approvalDecision.isOpen || itemPickerOpen || pickerLoading;
  const hasUnsavedChanges = hasOrderEditorChanges(currentForm);
  const actionGroups = [
    { label: t("Approval"), actions: currentApprovalTask && pendingApproval ? [
      { label: t("Return with Comments"), icon: FiCornerUpLeft, onClick: () => handleApprovalAction("return") },
      { label: t("Reject"), icon: FiXCircle, onClick: () => handleApprovalAction("reject") },
    ] : [] },
    { label: t("Document"), actions: [{ label: t("Generate PDF Draft"), icon: FiFileText, onClick: () => {
      const token = localStorage.getItem("palantir_token");
      const baseUrl = process.env.REACT_APP_API_URL || "";
      window.open(`${baseUrl}/api/purchasing/order-pdf/${currentForm.id}/pdf-draft?send=false&token=${token}`, "_blank");
    } }] },
    { label: t("Order management"), actions: [
      ...(canRecallOrder ? [{ label: t("Recover Order"), icon: FiRotateCcw, onClick: handleRecall }] : []),
      ...(canCancelOrder ? [{ label: editText("Cancel purchase order"), icon: FiSlash, onClick: handleCancelOrder }] : []),
    ] },
    { label: t("Danger zone"), danger: true, actions: canDeleteOrder ? [
      { label: t("Delete Order"), icon: FiTrash2, onClick: handleDeleteOrder },
    ] : [] },
  ];

  const popupCss = `
    .eop-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
    .eop-modal{width:min(1460px,98vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-muted);box-shadow:var(--pu-shadow)}
    .eop-modal > h5{display:none}
    .eop-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px 14px;border-bottom:1px solid var(--pu-border)}
    .eop-headMain{flex:1;min-width:0}
    .eop-headActions{display:flex;align-items:center;gap:10px;flex-shrink:0}
    .eop-title{margin:0;font-size:clamp(1.25rem,2vw,1.75rem);font-weight:900;line-height:1.05}
    .eop-sub{margin-top:8px;font-size:.9rem;color:var(--pu-muted)}
    .eop-badge{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-muted);font-size:.72rem;font-weight:800;letter-spacing:.08em}
    .eop-closeBtn{width:40px;min-width:40px;height:40px;border-radius:14px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-muted);font-size:.92rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
    .eop-closeBtn:hover{transform:none;background:var(--pu-soft);border-color:var(--pu-border);box-shadow:none}
    .eop-body{padding:16px 24px 24px}
    .eop-section{border:1px solid var(--pu-border);border-radius:20px;padding:18px;background:var(--pu-soft);box-shadow:none}
    .eop-section + .eop-section{margin-top:16px}
    .eop-sectionTitle{margin:0 0 4px;font-size:1rem;font-weight:850}
    .eop-sectionSub{margin:0 0 14px;color:var(--pu-muted);font-size:.82rem}
    .eop-row{display:grid;gap:16px}
    .eop-row--meta{grid-template-columns:minmax(160px,.8fr) minmax(240px,1.2fr) minmax(180px,.8fr) minmax(180px,.7fr) minmax(260px,1.5fr)}
    .eop-field input{width:100%}
    .eop-modal .form-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
    .eop-modal .form-control,.eop-modal .form-select{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-surface)!important;color:var(--pu-muted)!important;padding:10px 12px;min-height:42px}
    .eop-modal .bg-secondary.form-control,.eop-modal .bg-secondary.form-select{background:var(--pu-surface)!important;border-color:var(--pu-border)!important}
    .eop-alert{border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);border-radius:12px;padding:10px 12px;font-size:.84rem;margin-bottom:12px}
    .eop-loading{border:1px dashed var(--pu-border);border-radius:20px;padding:22px 20px;background:var(--pu-soft);color:var(--pu-muted);font-size:.9rem}
    .eop-linesTop{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin:0 0 12px}
    .eop-linesTop h6{margin:0;font-size:1rem;font-weight:850}
    .eop-linesTop p{margin:6px 0 0;color:var(--pu-muted);font-size:.8rem}
    .eop-tableShell{border:1px solid var(--pu-border);border-radius:22px;padding:14px 16px 16px;background:var(--pu-soft);box-shadow:none}
    .eop-tableWrap{overflow:hidden;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
    .eop-table{width:100%;border-collapse:separate;border-spacing:0;min-width:0;table-layout:fixed;background:var(--pu-soft)!important;border:none!important}
    .eop-table th,.eop-table td{padding:12px 14px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
    .eop-table th{position:sticky;top:0;z-index:2;background:#223a7a;color:#ffffff;font-size:.73rem;text-transform:uppercase;letter-spacing:.06em;font-weight:800}
    .eop-table th:first-child{border-top-left-radius:14px}
    .eop-table th:last-child{border-top-right-radius:14px}
    .eop-table tbody tr{background:var(--pu-soft)}
    .eop-table tbody tr:nth-child(even){background:var(--pu-soft)}
    .eop-table tbody tr:last-child td{border-bottom:none}
    .eop-itemName{display:block;font-size:.9rem;font-weight:750;color:var(--pu-muted);line-height:1.25;overflow-wrap:anywhere}
    .eop-itemMeta{display:block;margin-top:4px;font-size:.72rem;color:var(--pu-muted)}
    .eop-lineMetaValue{font-size:.88rem;font-weight:750;color:var(--pu-muted)}
    .eop-tableInput{width:100%!important;min-width:0}
    .eop-rowActions{display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap}
    .eop-btn{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-muted);padding:8px 13px;font-size:.86rem;font-weight:750;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease;white-space:nowrap}
    .eop-btn:hover{transform:none;background:var(--pu-soft);box-shadow:none}
    .eop-btn:disabled{opacity:1;cursor:not-allowed;transform:none;box-shadow:none}
    .eop-btn--primary{border-color:var(--pu-border);background:#223a7a;color:#ffffff}
    .eop-btn--danger{border-color:var(--pu-danger);color:var(--pu-danger);background:var(--pu-danger-bg)}
    .eop-btn--warning{border-color:var(--pu-border);color:var(--pu-muted);background:var(--pu-soft)}
    .eop-btn--success{border-color:var(--pu-success);color:var(--pu-success);background:var(--pu-success-bg)}
    .eop-btn--cyan{border-color:var(--pu-border);color:var(--pu-muted);background:var(--pu-soft)}
    .eop-btn--sm{padding:6px 10px;font-size:.78rem}
    .eop-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:18px}
    .eop-approvalNotice{display:inline-flex;align-items:center;min-height:38px;border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-muted);padding:8px 12px;font-size:.82rem;font-weight:800}
    .eop-emptyState{border:1px dashed var(--pu-border);border-radius:20px;padding:22px 20px;background:var(--pu-soft)}
    .eop-emptyState strong{display:block;font-size:1rem}
    .eop-emptyState span{display:block;margin-top:6px;color:var(--pu-muted);font-size:.86rem}
    .eop-cardList{display:grid;gap:12px}
    .eop-card{border:1px solid var(--pu-border)!important;border-radius:18px!important;padding:14px!important;background:var(--pu-soft)!important}
    .eop-cardTop{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:12px}
    .eop-cardTitle{font-size:.84rem;font-weight:800;color:var(--pu-muted);text-transform:uppercase;letter-spacing:.05em}
    .eop-lineGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    .eop-lineGrid--single{grid-template-columns:minmax(0,1fr)}
    .eop-lineMeta{border-radius:14px;padding:10px 12px;background:var(--pu-soft);border:1px solid var(--pu-border)}
    .eop-lineMetaLabel{display:block;margin-bottom:4px;color:var(--pu-muted);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
    @media (max-width:1180px){.eop-row--meta{grid-template-columns:repeat(2,minmax(0,1fr))}.eop-row--meta .eop-field:last-child{grid-column:1 / -1}}
    @media (max-width:640px){.eop-wrap{padding:8px;align-items:flex-start}.eop-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.eop-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.eop-headActions{gap:8px;justify-content:flex-end;align-self:start}.eop-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.eop-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.eop-badge{padding:5px 8px;font-size:.64rem}.eop-closeBtn{width:auto;min-width:76px;height:36px;padding:0 12px;border-radius:12px}.eop-body{padding:12px 14px 14px}.eop-section{padding:14px}.eop-row--meta,.eop-lineGrid{grid-template-columns:minmax(0,1fr)}.eop-linesTop{flex-direction:column;align-items:stretch}.eop-actions{flex-direction:column}.eop-actions .eop-btn{width:100%}.eop-rowActions{justify-content:stretch}.eop-rowActions .eop-btn{flex:1}}
  `;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center eop-wrap pilcore-purchasing-overlay pilcore-document-popup" data-theme={themeId}
      style={{ background: "rgba(0,0,0,0.85)", zIndex: 9999 }}
    >
      <style>
        {`
          input[type=number]::-webkit-inner-spin-button,
          input[type=number]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type=number] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <style>{popupCss}</style>

      <div
        className="eop-modal" role="dialog" aria-modal="true" aria-label={t("Edit Purchase Order")}
        style={{
          width: "96vw",
          maxWidth: 1380,
          maxHeight: "92vh",
          overflowY: "auto",
          border: "1px solid #444",
          padding: 0,
        }}
      >
        <h5 className="fw-bold mb-3 text-info">{t("Edit Purchase Order")}</h5>
        <div className={pendingApproval ? "eop-head eop-head--pending" : "eop-head"}>
          <div className="eop-headMain">
            <h5 className="eop-title">{pendingApproval
              ? <><FiClock aria-hidden="true" /> {approvalText("Pending approval")}</>
              : isMobile ? t("Edit Order") : t("Edit Purchase Order")}</h5>
            <div className="eop-sub">
              {pendingApproval ? [currentForm.order_code, currentForm.supplier?.name].filter(Boolean).join(" · ")
                : currentForm?.supplier?.name ? t("{0} · Update delivery, notes and order lines without leaving the order flow.", { 0: currentForm.supplier.name })
                : t("Update delivery, notes and order lines without leaving the order flow.")}
            </div>
            {pendingApproval && pendingApproverNames.length > 0 && <div className="eop-pendingWho">
              <strong>{approvalText("Awaiting decision from")}</strong> {pendingApproverNames.join(", ")}
            </div>}
          </div>
          <div className="eop-headActions">
            {!pendingApproval && <span className="eop-badge">{documentStatus(currentForm?.status || "loading", t)}</span>}
            <button
              type="button"
              className="eop-closeBtn"
              aria-label={t("Close edit purchase order popup")}
              title={t("Close")}
              onClick={handleClose}
              disabled={sendConfirmationOpen || documentDialogOpen || approvalDecision.isOpen || isSending || isSaving || itemPickerOpen || pickerLoading || savingLines > 0}
            >
              {isMobile ? t("Close") : "X"}
            </button>
          </div>
        </div>

        <fieldset className="eop-body" disabled={sendConfirmationOpen || documentDialogOpen || approvalDecision.isOpen || isSending || isSaving || itemPickerOpen || pickerLoading} style={{ border: 0, margin: 0, minWidth: 0 }}>
          {error && <div className="eop-alert" role="alert">{t(error)}</div>}

          {!currentForm ? (
            <div className="eop-loading">{loading ? t("Loading order...") : t("Preparing order...")}</div>
          ) : (
            <>
              <div className="eop-section">
                <div className="eop-detailsTop">
                  <div>
                    <h6 className="eop-sectionTitle">{t("Order Details")}</h6>
                    <p className="eop-sectionSub">{t("Review the header, adjust delivery or notes, then edit the existing supplier lines.")}</p>
                  </div>
                  <div className="eop-detailsActions">
                    {canEditOrder && hasUnsavedChanges && <span className="eop-unsaved" role="status">{t("Unsaved changes")}</span>}
                    {canEditOrder && hasUnsavedChanges && <button type="button" className="eop-btn eop-btn--primary" onClick={handleSave} disabled={actionsBusy}>{t("Save Changes")}</button>}
                    {canReviewAndSubmit && <button type="button" className="eop-btn eop-btn--success" onClick={handleReviewAndSubmit} disabled={actionsBusy}>{editText("Review and submit for approval")}</button>}
                    {canSendToSupplier && <button type="button" className="eop-btn eop-btn--primary" onClick={openSendConfirmation} disabled={actionsBusy}>{t("Send to Supplier")}</button>}
                    {currentApprovalTask && pendingApproval && <button type="button" className="eop-btn eop-btn--success" onClick={() => handleApprovalAction("approve")} disabled={actionsBusy}>{t("Approve")}</button>}
                    <PurchaseOrderActions key={currentForm.id} label={t("Actions")} groups={actionGroups} disabled={actionsBusy} />
                  </div>
                </div>

                <div className="eop-row eop-row--meta">
                  <div className="eop-field">
                    <label className="form-label">{t("Order Code")}</label>
                    <input
                      type="text"
                      readOnly
                      value={currentForm.order_code || ""}
                      className="form-control bg-secondary text-white border-0"
                    />
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Supplier")}</label>
                    <input
                      type="text"
                      readOnly
                      value={currentForm.supplier?.name || t("(not assigned)")}
                      className="form-control bg-secondary text-white border-0"
                    />
                    {minimumLabel && <small>{minimumLabel}</small>}
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Expected Delivery")}</label>
                    <input
                      type="date"
                      name="expected_delivery_date"
                      disabled={!canEditOrder}
                      value={currentForm.expected_delivery_date || ""}
                      onChange={handleChange}
                      className="form-control bg-black text-white border-secondary"
                    />
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Destination Location")}</label>
                    <select
                      name="destination_location_id"
                      disabled={!canEditOrder}
                      value={currentForm.destination_location_id || ""}
                      onChange={handleChange}
                      className="form-select bg-black text-white border-secondary"
                    >
                      <option value="">{t("Automatic by item")}</option>
                      {inventoryLocations.map((location) => (
                        <option key={location.id} value={String(location.id)}>
                          {getLocationName(location, languageId)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Initiated By")}</label>
                    <input
                      type="text"
                      readOnly
                      value={formatDocumentInitiator(currentForm)}
                      className="form-control bg-secondary text-white border-0"
                    />
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Total (EUR)")}</label>
                    <input
                      type="text"
                      readOnly
                      value={formatMoney(calculateTotal())}
                      className="form-control bg-secondary text-white fw-bold text-end border-0"
                    />
                  </div>

                  <div className="eop-field">
                    <label className="form-label">{t("Notes")}</label>
                    <input
                      type="text"
                      name="notes"
                      disabled={!canEditOrder}
                      value={currentForm.notes || ""}
                      onChange={handleChange}
                      className="form-control bg-black text-white border-secondary"
                      placeholder={t("Internal notes...")}
                    />
                  </div>
                </div>
              </div>

              <div className="eop-section">
                <div className="eop-linesTop">
                  <div>
                    <h6>{t("Order Lines")}</h6>
                    <p>{canAddItems ? editText("Add items, or choose Edit on a line to change its quantity or price.") : t("Edit quantities or unit prices without the old inline controls getting in the way.")}</p>
                    {confirmedUnsent && canEditOrder && <p>{editText("Save changes to apply all item edits together. The updated order follows the approval rules again.")}</p>}
                  </div>
                  {canAddItems && <button type="button" className="eop-btn eop-btn--primary"
                    onClick={openItemPicker} disabled={savingLines > 0}>
                    {editText("+ Add Item")}
                  </button>}
                </div>

                {currentForm.lines?.length ? (
                  isMobile ? (
                    <div className="eop-cardList">
                      {currentForm.lines.map((line, i) => {
                        const isEditable = canEditOrder && (!line.__locked || line.__editable);
                        return (
                          <div key={line._destinationKey || line.id || i} className="eop-card">
                            <div className="eop-cardTop">
                              <div className="eop-cardTitle">{t("Line")} {i + 1}</div>
                              <div className="eop-rowActions">
                                {isEditable ? (
                                  <button
                                    type="button"
                                    className="eop-btn eop-btn--success eop-btn--sm"
                                    onClick={() => handleSaveLine(i)}
                                    disabled={savingLines > 0}
                                  >{confirmedUnsent ? editText("Done") : t("Save")}</button>
                                ) : (
                                  <button
                                    type="button"
                                    className="eop-btn eop-btn--warning eop-btn--sm"
                                    disabled={!canEditOrder}
                                    onClick={() => setLineEditable(i, true)}
                                  >{t("Edit")}</button>
                                )}
                                <button
                                  type="button"
                                  className="eop-btn eop-btn--danger eop-btn--sm"
                                  onClick={() => handleDeleteLine(i)}
                                  disabled={!canEditOrder || savingLines > 0}
                                >{t("Remove")}</button>
                              </div>
                            </div>

                            <div className="eop-lineGrid eop-lineGrid--single">
                              <div className="eop-lineMeta">
                                <span className="eop-lineMetaLabel">{t("Item")}</span>
                                <span className="eop-lineMetaValue">{getLineItemLabel(line)}</span>
                                {renderLineLocation(line, i)}
                              </div>
                            </div>

                            <div className="eop-lineGrid" style={{ marginTop: 12 }}>
                              <div className="eop-lineMeta">
                                <span className="eop-lineMetaLabel">{t("Qty")}</span>
                                {isEditable ? (
                                  <OrderLineNumberInput
                                    aria-label={t("Qty")}
                                    value={line.quantity ?? ""}
                                    onChange={(event) => handleLineChange(i, "quantity", event.target.value)}
                                    className="form-control bg-black text-white border-secondary text-center eop-tableInput"
                                  />
                                ) : (
                                  <span className="eop-lineMetaValue">{formatNumber(line.quantity)}</span>
                                )}
                              </div>

                              <div className="eop-lineMeta">
                                <span className="eop-lineMetaLabel">{t("Packaging")}</span>
                                <span className="eop-lineMetaValue">{getPackagingLabel(line)}</span>
                              </div>

                              <div className="eop-lineMeta">
                                <span className="eop-lineMetaLabel">{t("Unit EUR")}</span>
                                {isEditable ? (
                                  <OrderLineNumberInput
                                    aria-label={t("Unit EUR")}
                                    value={line.unit_price ?? ""}
                                    readOnly={Boolean(line.tender_id)}
                                    onChange={(event) => handleLineChange(i, "unit_price", event.target.value)}
                                    className="form-control bg-black text-white border-secondary text-end eop-tableInput"
                                  />
                                ) : (
                                  <span className="eop-lineMetaValue">
                                    {formatMoney(line.unit_price)}
                                  </span>
                                )}
                                <TenderPriceLabel line={line} />
                                {line.tender_id && <small>{t("Tender price is fixed. The agreed price remains unchanged.")}</small>}
                              </div>

                              <div className="eop-lineMeta">
                                <span className="eop-lineMetaLabel">{t("Line Total")}</span>
                                <span className="eop-lineMetaValue">{getLineTotal(line)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="eop-tableShell">
                      <div className="eop-tableWrap">
                        <table className="eop-table">
                          <thead>
                            <tr>
                              <th style={{ width: "40%" }}>{t("Item")}</th>
                              <th style={{ width: "10%", textAlign: "center" }}>{t("Qty")}</th>
                              <th style={{ width: "18%" }}>{t("Packaging")}</th>
                              <th style={{ width: "10%", textAlign: "right" }}>{t("Unit EUR")}</th>
                              <th style={{ width: "12%", textAlign: "right" }}>{t("Line Total")}</th>
                              <th style={{ width: "10%", textAlign: "right" }}>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentForm.lines.map((line, i) => {
                              const isEditable = canEditOrder && (!line.__locked || line.__editable);
                              return (
                                <tr key={line._destinationKey || line.id || i}>
                                  <td>
                                    <span className="eop-itemName">{getLineItemLabel(line)}</span>
                                    {renderLineLocation(line, i)}
                                    <span className="eop-itemMeta">
                                      {line.item?.supplier_code
                                        ? t("Supplier code: {0}", { 0: line.item.supplier_code })
                                        : line.description || t("Purchase order line")}
                                    </span>
                                  </td>

                                  <td style={{ textAlign: "center" }}>
                                    {isEditable ? (
                                      <OrderLineNumberInput
                                        aria-label={t("Qty")}
                                        value={line.quantity ?? ""}
                                        onChange={(event) => handleLineChange(i, "quantity", event.target.value)}
                                        className="form-control bg-black text-white border-secondary text-center eop-tableInput"
                                      />
                                    ) : (
                                      <span className="eop-lineMetaValue">{formatNumber(line.quantity)}</span>
                                    )}
                                  </td>

                                  <td>
                                    <span className="eop-lineMetaValue">{getPackagingLabel(line)}</span>
                                  </td>

                                  <td style={{ textAlign: "right" }}>
                                    {isEditable ? (
                                      <OrderLineNumberInput
                                        aria-label={t("Unit EUR")}
                                        value={line.unit_price ?? ""}
                                        readOnly={Boolean(line.tender_id)}
                                        onChange={(event) => handleLineChange(i, "unit_price", event.target.value)}
                                        className="form-control bg-black text-white border-secondary text-end eop-tableInput"
                                      />
                                    ) : (
                                      <span className="eop-lineMetaValue">
                                        {formatMoney(line.unit_price)}
                                      </span>
                                    )}
                                    <TenderPriceLabel line={line} />
                                    {line.tender_id && <small>{t("Tender price is fixed. The agreed price remains unchanged.")}</small>}
                                  </td>

                                  <td style={{ textAlign: "right" }}>
                                    <span className="eop-lineMetaValue">{getLineTotal(line)}</span>
                                  </td>

                                  <td style={{ textAlign: "right" }}>
                                    <div className="eop-rowActions">
                                      {isEditable ? (
                                        <button
                                          type="button"
                                          className="eop-btn eop-btn--success eop-btn--sm"
                                          onClick={() => handleSaveLine(i)}
                                          disabled={savingLines > 0}
                                        >{confirmedUnsent ? editText("Done") : t("Save")}</button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="eop-btn eop-btn--warning eop-btn--sm"
                                          disabled={!canEditOrder}
                                    onClick={() => setLineEditable(i, true)}
                                        >{t("Edit")}</button>
                                      )}
                                      <button
                                        type="button"
                                        className="eop-btn eop-btn--danger eop-btn--sm"
                                        onClick={() => handleDeleteLine(i)}
                                        disabled={!canEditOrder || savingLines > 0}
                                      >{t("Remove")}</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="eop-emptyState">
                    <strong>{t("No lines.")}</strong>
                    <span>{t("This purchase order has no order lines yet.")}</span>
                    {canAddItems && <span>{editText("Choose an item from this supplier to add it to the order.")}</span>}
                  </div>
                )}
              </div>

              <POApprovalHistory runs={currentForm.approval_runs || []} />
              {currentForm.status === "pending_approval" && currentForm.procurement_review_required === true && <p className="eop-approvalNotice">{editText("This pending approval has no valid procurement review. Recover the order to draft before reviewing and submitting it again.")}</p>}
            </>
          )}

        </fieldset>
      </div>
      <PurchasingDocumentDialog
        isOpen={sendConfirmationOpen}
        title={t("Send Purchase Order")}
        eyebrow={t("Pilcore · Purchasing")}
        message={
          <>
            <p className="pcd-message">{t("Send Purchase Order")} <strong>{currentForm?.order_code}</strong> {languageId === "es" ? "a" : "to"} {currentForm?.supplier?.name}?
            </p>
            <p className="pcd-message" style={{ overflowWrap: "anywhere" }}>
              {currentForm?.supplier?.email}
            </p>
            <p className="pcd-message">{t("Your changes will be saved before sending.")}</p>
            {sendError && <div className="eop-alert" role="alert">{sendError}</div>}
          </>
        }
        cancelLabel={t("Cancel")}
        confirmLabel={isSending ? t("Saving and sending...") : t("Save and Send")}
        onConfirm={handleSendToSupplier}
        onClose={() => setSendConfirmationOpen(false)}
        isBusy={isSending}
        confirmDisabled={!canSendToSupplier}
        tone="primary"
        zIndex={10050}
      />
      <PurchaseOrderItemPicker isOpen={itemPickerOpen} items={pickerItems}
        lines={currentForm?.lines || []} supplierId={currentForm?.supplier_id} units={units}
        businessUnitId={currentUser?.active_business_unit_id} date={currentForm?.order_date} currency={currentForm?.currency || "EUR"}
        onSelect={addItem} onClose={() => setItemPickerOpen(false)} />
      {dialog}
      {approvalDecision.dialog}
    </div>
  );
}
